import React from 'react';
import SEOHead from '../components/SEOHead';
import JsonLd from '../components/JsonLd';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import HeroDiagram from '../components/HeroDiagram';
import {
  Headphones,
  Phone,
  ArrowRight,
  Shield,
  Zap,
  Users,
  CheckCircle2,
  Play,
  Mic,
  BarChart2,
  Database,
  Search,
  LineChart,
  MessageSquare,
  Building2,
  TrendingUp,
  Clock,
  Award
} from 'lucide-react';

/* ─── Placeholder data ─────────────────────────────────────────────────────── */

const voiceCapabilities = [
  {
    icon: Clock,
    value: '<2s',
    label: 'Call Pick-Up Time',
    desc: 'Calls answered in under 2 seconds. Zero hold time, every time.',
  },
  {
    icon: Mic,
    value: '24/7',
    label: 'Always Available',
    desc: 'No shift patterns, no peak-hour queues. Your AI agent never sleeps.',
  },
  {
    icon: TrendingUp,
    value: '70%',
    label: 'Queries Auto-Resolved',
    desc: 'Most common requests resolved on first contact without human involvement.',
  },
  {
    icon: Building2,
    value: '30+',
    label: 'Languages Supported',
    desc: 'Serve global workforces and customer bases in their native language.',
  },
];

const howItWorks = [
  {
    step: '01',
    title: 'Connect Your Data & Systems',
    description:
      'Integrate Digian.ai with your existing CRM, ITSM, data warehouse, and telephony stack in minutes. Pre-built connectors for 50+ enterprise systems.',
    icon: Database,
  },
  {
    step: '02',
    title: 'Deploy AI Agents Instantly',
    description:
      'Configure and launch AI voice or chat agents trained on your data, policies, and workflows, with no ML expertise required.',
    icon: Zap,
  },
  {
    step: '03',
    title: 'Measure, Learn & Scale',
    description:
      'Track resolution rates, call volumes, and cost savings in real time. Continuously improve agent performance with built-in analytics.',
    icon: BarChart2,
  },
];


const integrations = [
  'Salesforce', 'ServiceNow', 'Snowflake', 'Databricks',
  'Microsoft Azure', 'AWS', 'Google Cloud', 'SAP',
  'Twilio', 'Genesys', 'Zendesk', 'HubSpot',
];

const products = [
  {
    id: 'ai-voice-agent',
    category: 'AI Voice Agent',
    name: 'AI Voice IT Support',
    description: 'Intelligent voice-based IT support with 24/7 availability, automated ticket resolution, and seamless ITSM integration. Resolve password resets, onboarding, and Tier 1 tickets without a single human agent.',
    icon: Headphones,
    gradient: 'from-indigo-500 to-purple-500',
    href: '/ai-voice-agent',
    highlights: ['Auto-resolve Tier 1 tickets', 'ITSM integration (ServiceNow, Jira)', '99.9% uptime SLA'],
    featured: true,
  },
  {
    id: 'ai-customer-care',
    category: 'AI Voice Agent',
    name: 'AI Customer Care',
    description: 'Advanced voice assistant for customer service excellence: multi-channel, multi-language, always available.',
    icon: Phone,
    gradient: 'from-purple-500 to-pink-500',
    href: '/ai-customer-care',
    highlights: ['Multi-language support', 'Sentiment detection', 'CRM sync'],
    featured: false,
  },
];

const useCases = [
  { icon: Shield, label: 'Password Reset & Unlock', href: '/use-cases/password-reset' },
  { icon: Users, label: 'Employee Onboarding', href: '/use-cases/employee-onboarding' },
  { icon: MessageSquare, label: 'IT Ticket Automation', href: '/use-cases/hardware-support' },
  { icon: Phone, label: 'Order Status & Returns', href: '/use-cases/wismo' },
];

/* ─── Component ─────────────────────────────────────────────────────────────── */

const HomePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead page="home" />
      <JsonLd schema={{
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            "name": "Digian",
            "url": "https://digian.ai",
            "logo": "https://digian.ai/favicon.png",
            "description": "Enterprise AI voice agents, workflow automation, and intelligent customer care solutions for modern businesses.",
            "sameAs": [
              "https://www.linkedin.com/company/digian-ai"
            ]
          },
          {
            "@type": "WebSite",
            "name": "Digian",
            "url": "https://digian.ai"
          }
        ]
      }} />

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-white">
        {/* Clean geometric background — indigo radial glow top-left, subtle dot grid */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_-10%_-10%,rgba(99,102,241,0.12),transparent)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_50%_40%_at_110%_80%,rgba(139,92,246,0.08),transparent)]" />
        <div className="absolute inset-0"
          style={{ backgroundImage: 'radial-gradient(circle, #e0e7ff 1px, transparent 1px)', backgroundSize: '28px 28px', opacity: 0.4 }}
        />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-8 lg:py-12">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-semibold px-4 py-2 rounded-full uppercase tracking-wide">
                <Award className="w-3.5 h-3.5" />
                AI Voice Agent for Enterprise IT &amp; Customer Care
              </div>

              <div className="space-y-4">
                <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-[1.1] tracking-tight" data-testid="hero-heading">
                  Your enterprise{' '}
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-violet-600">
                    AI Voice Agent
                  </span>
                  {' '}answering calls in under 2 seconds
                </h1>
                <p className="text-lg text-slate-500 leading-relaxed max-w-lg" data-testid="hero-subheading">
                  Automate IT support and customer care with an AI voice agent that resolves 70% of queries without human involvement, 24/7, in 30+ languages.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/demo" data-testid="hero-request-demo-btn">
                  <Button size="lg" className="group bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-200">
                    Request a Demo
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/ai-voice-agent" data-testid="hero-explore-products-btn">
                  <Button size="lg" variant="outline" className="group border-slate-200 hover:border-indigo-300 hover:bg-indigo-50">
                    <Play className="mr-2 w-4 h-4 text-indigo-600" />
                    Explore Products
                  </Button>
                </Link>
              </div>

              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-400 pt-1">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /> No credit card required</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /> 14-day free trial</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" /> Enterprise-grade security</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="relative flex justify-center lg:justify-end"
            >
              {/* Decorative glow behind diagram */}
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-400/15 to-violet-400/10 rounded-3xl blur-3xl scale-95" />
              <div className="relative w-full rounded-2xl bg-white/60 backdrop-blur-sm border border-white/80 shadow-2xl shadow-indigo-200/50 p-4 md:p-6">
                <HeroDiagram />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── AI Voice Capabilities Bar ─────────────────────────────────────── */}
      <section className="bg-white border-y border-slate-100 py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-6">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-2">AI Voice Performance</p>
            <h2 className="text-2xl font-bold text-slate-900">Built for enterprise call volumes, without the wait</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {voiceCapabilities.map((cap, i) => {
              const Icon = cap.icon;
              return (
                <motion.div
                  key={cap.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-slate-50 rounded-xl border border-slate-200 p-6 text-center hover:border-indigo-300 transition-colors"
                >
                  <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-indigo-600" />
                  </div>
                  <p className="text-3xl font-extrabold text-indigo-600 mb-1">{cap.value}</p>
                  <p className="text-sm font-semibold text-slate-900 mb-2">{cap.label}</p>
                  <p className="text-xs text-slate-500 leading-relaxed">{cap.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Products Grid ─────────────────────────────────────────────────── */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Our Products</p>
            <h2 className="text-4xl font-bold text-slate-900 mb-4" data-testid="products-section-heading">
              AI Voice Agents for IT &amp; Customer Care
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Deploy a voice agent that handles real workflows — not just answers questions. Built for enterprise IT teams and customer care operations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {products.map((product, i) => {
              const Icon = product.icon;
              return (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Link to={product.href} data-testid={`product-card-${product.id}`} className="block h-full">
                    <div className={`group h-full rounded-xl border p-8 hover:shadow-lg transition-all duration-300 flex flex-col ${product.featured ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-slate-200 hover:border-indigo-400'}`}>
                      {product.featured && (
                        <span className="inline-block text-xs font-bold uppercase tracking-widest bg-white/20 text-white px-3 py-1 rounded-full mb-4 self-start">
                          Core Product
                        </span>
                      )}
                      {!product.featured && (
                        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">{product.category}</p>
                      )}
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${product.gradient} flex items-center justify-center mb-5 ${product.featured ? 'opacity-90' : ''}`}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <h3 className={`text-xl font-bold mb-2 transition-colors ${product.featured ? 'text-white' : 'text-slate-900 group-hover:text-indigo-600'}`}>
                        {product.name}
                      </h3>
                      <p className={`leading-relaxed mb-5 text-sm flex-1 ${product.featured ? 'text-indigo-100' : 'text-slate-600'}`}>
                        {product.description}
                      </p>
                      <ul className="space-y-1.5 mb-6">
                        {product.highlights.map((h) => (
                          <li key={h} className={`flex items-center gap-2 text-xs ${product.featured ? 'text-indigo-100' : 'text-slate-600'}`}>
                            <CheckCircle2 className={`w-3.5 h-3.5 flex-shrink-0 ${product.featured ? 'text-white' : 'text-indigo-500'}`} />
                            {h}
                          </li>
                        ))}
                      </ul>
                      <div className={`flex items-center font-medium text-sm mt-auto group-hover:translate-x-1 transition-transform ${product.featured ? 'text-white' : 'text-indigo-600'}`}>
                        Learn More <ArrowRight className="ml-1.5 w-4 h-4" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>

        </div>
      </section>

      {/* ── Use Cases Strip ───────────────────────────────────────────────── */}
      <section className="py-8 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-center text-xs font-semibold uppercase tracking-widest text-slate-400 mb-8">Key Use Cases</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {useCases.map((uc) => {
              const Icon = uc.icon;
              return (
                <Link key={uc.label} to={uc.href}>
                  <div className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-all group">
                    <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0 group-hover:bg-indigo-100">
                      <Icon className="w-4 h-4 text-indigo-600" />
                    </div>
                    <span className="text-sm font-medium text-slate-700 group-hover:text-indigo-700">{uc.label}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── How It Works ──────────────────────────────────────────────────── */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">How It Works</p>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Up and running in days, not months</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A simple three-step process from integration to full AI deployment, designed for enterprise IT teams.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* connector line (desktop) */}
            <div className="hidden md:block absolute top-10 left-1/6 right-1/6 h-px bg-gradient-to-r from-indigo-200 via-violet-300 to-indigo-200 z-0" style={{ left: '20%', right: '20%' }} />

            {howItWorks.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                  className="relative bg-white rounded-2xl border border-slate-200 p-8 z-10"
                >
                  <div className="flex items-center gap-4 mb-5">
                    <span className="text-4xl font-black text-indigo-100">{step.step}</span>
                    <div className="w-12 h-12 rounded-xl bg-indigo-600 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>


      {/* ── Integrations Strip ────────────────────────────────────────────── */}
      <section className="py-10 bg-slate-50 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-center text-xs font-semibold uppercase tracking-widest text-slate-400 mb-8">
            Integrates with your existing stack {/* PLACEHOLDER: add real integration logos */}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            {integrations.map((name) => (
              <div
                key={name}
                className="px-5 py-2.5 bg-white rounded-lg border border-slate-200 text-sm font-medium text-slate-500 hover:border-indigo-300 hover:text-indigo-600 transition-colors"
              >
                {/* PLACEHOLDER: replace with <img src={logoUrl} alt={name} className="h-6 object-contain" /> */}
                {name}
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-slate-400 mt-6">
            + 50+ integrations available ·<Link to="/contact" className="text-indigo-600 hover:underline">Request a custom integration</Link>
          </p>
        </div>
      </section>

      {/* ── Trust Pillars ─────────────────────────────────────────────────── */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Shield, label: 'Enterprise Security', description: 'End-to-end encryption, role-based access control, audit logging, and GDPR-aligned data handling.' },
              { icon: Zap, label: 'High-Performance Architecture', description: '99.9% SLA backed uptime with global CDN, auto-scaling, and zero-downtime deployments.' },
              { icon: Users, label: 'Dedicated Success Team', description: 'Onboarding specialists, dedicated CSMs, and 24/7 enterprise support included in every plan.' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-start gap-4"
                  data-testid={`trust-indicator-${i}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">{item.label}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{item.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-r from-indigo-600 to-violet-600 py-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h2 className="text-4xl font-bold text-white" data-testid="cta-heading">
              See the AI Voice Agent in Action
            </h2>
            <p className="text-xl text-indigo-100">
              Watch it handle a real IT support call — password reset, ticket creation, employee onboarding — live in 30 minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo" data-testid="cta-get-started-btn">
                <Button size="lg" variant="secondary" className="text-indigo-600 font-semibold">
                  Book a Demo
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/contact" data-testid="cta-contact-sales-btn">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-indigo-600">
                  Talk to Sales
                </Button>
              </Link>
            </div>
            <p className="text-indigo-200 text-sm">No commitment required · Personalised to your stack · Response within 24 hours</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
